
import { Component, inject, signal } from '@angular/core';
import { FrontendUrlsService } from '@shared-angular/services/frontend-urls.service';
import { FormsModule } from '@angular/forms';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { ProjectApiTokenScopes } from '@shared/index';
import dayjs from 'dayjs';
import { Duration } from 'dayjs/plugin/duration';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { ProjectSubmenuComponent } from '../../../../../../project-submenu/project-submenu.component';
import { ProjectApiTokenInFrontend, ProjectApiTokenService } from '../../../../../../shared/services/project-api-token.service';

type ProjectApiTokenScopeDescription = {
  scope: ProjectApiTokenScopes,
  desciption: string
}
type Expiration = {
  duration: Duration,
  description: string
}

@Component({
  selector: 'app-project-apitoken-tab',
  imports: [
    FormsModule,
    TooltipModule,
    MessageModule,
    InputTextModule,
    CardModule,
    CheckboxModule,
    SelectModule,
    ButtonModule,
    TableModule,
    ProjectSubmenuComponent
  ],
  standalone: true,
  templateUrl: './project-apitoken-tab.component.html',
  styleUrl: './project-apitoken-tab.component.scss'
})
export class ProjectApitokenTabComponent {
  protected readonly swaggerUrl = inject(FrontendUrlsService).bot('/swagger#/External%20Project%20API');

  public readonly projectApiTokenScopes: ProjectApiTokenScopeDescription[] = [
    { scope: "projectdoc:read", desciption: $localize`:@@projectdoc-read-description:Dokumente auflisten, herunterladen und Chunks abrufen` },
    { scope: "projectdoc:write", desciption: $localize`:@@projectdoc-write-description:Dokumente hochladen, ersetzen und löschen` },
    { scope: "projectdoc:chunk", desciption: $localize`:@@projectdoc-chunk-description:Chunking (Extraktion) für Dokumente anstoßen` },
    { scope: "bots:read", desciption: $localize`:@@bots-read-description:Bots des Projektes auflisten` },
    { scope: "bots:write", desciption: $localize`:@@bots-write-description:Bots erstellen, bearbeiten, löschen sowie Dokumente einem Bot zuweisen und entfernen` },
    { scope: "projectkeyval:read", desciption: $localize`:@@projectkeyval-read-description:Projekt KeyVal-Einträge lesen` },
    { scope: "projectkeyval:write", desciption: $localize`:@@projectkeyval-write-description:Projekt KeyVal-Einträge schreiben und löschen` },
    { scope: "skills:read", desciption: $localize`:@@skills-read-description:Skills (Wenn-Dann-Regeln, Online-Quellen, Webhooks) abfragen` },
    { scope: "skills:write", desciption: $localize`:@@skills-write-description:Skills (Wenn-Dann-Regeln, Online-Quellen, Webhooks) erstellen, bearbeiten und löschen` },
    { scope: "stages:read", desciption: $localize`:@@stages-read-description:Stages (Gesprächsphasen) abfragen` },
    { scope: "stages:write", desciption: $localize`:@@stages-write-description:Stages (Gesprächsphasen) erstellen, bearbeiten und löschen` },
    { scope: "fusion:read", desciption: $localize`:@@fusion-read-description:Fusion-Views abfragen` },
    { scope: "fusion:write", desciption: $localize`:@@fusion-write-description:Fusion-Views erstellen, bearbeiten und löschen` },
    { scope: "embedding:read", desciption: $localize`:@@embedding-read-description:Website-Crawls abfragen` },
    { scope: "embedding:write", desciption: $localize`:@@embedding-write-description:Website-Crawls starten, bearbeiten und löschen` },
    { scope: "changelog:read", desciption: $localize`:@@project-changelog-read-description:Änderungsverlauf der Bots des Projektes abfragen` },
    { scope: "stats:read", desciption: $localize`:@@stats-read-description:Nutzungsstatistiken und Feedback abfragen` }
  ];

  public readonly expirations: Expiration[] = [
    { duration: dayjs.duration({ days: 7 }), description: "7 " + $localize`:@@days:Tage` },
    { duration: dayjs.duration({ days: 30 }), description: "30 " + $localize`:@@days:Tage` },
    { duration: dayjs.duration({ days: 60 }), description: "60 " + $localize`:@@days:Tage` },
    { duration: dayjs.duration({ days: 90 }), description: "90 " + $localize`:@@days:Tage` },
    { duration: dayjs.duration({ days: 365 }), description: "365 " + $localize`:@@days:Tage` },
    { duration: dayjs.duration({ years: 5 }), description: "5 " + $localize`:@@years:Jahre` },
    { duration: dayjs.duration({ years: 100 }), description: $localize`:@@years-never:Verfällt nie` }
  ];

  private readonly _newTokenName = signal('');
  public get newTokenName(): string { return this._newTokenName(); }
  public set newTokenName(value: string) { this._newTokenName.set(value); }
  private readonly _newTokenScopeSet = signal(new Set<ProjectApiTokenScopes>());
  public get newTokenScopeSet(): Set<ProjectApiTokenScopes> { return this._newTokenScopeSet(); }
  public set newTokenScopeSet(value: Set<ProjectApiTokenScopes>) { this._newTokenScopeSet.set(value); }
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  private readonly _newTokenExpiration = signal<Duration>(this.expirations[0]!.duration);
  public get newTokenExpiration(): Duration { return this._newTokenExpiration(); }
  public set newTokenExpiration(value: Duration) { this._newTokenExpiration.set(value); }

  private readonly _accessToken = signal<string | undefined>(undefined);
  public get accessToken(): string | undefined { return this._accessToken(); }
  public set accessToken(value: string | undefined) { this._accessToken.set(value); }

  constructor(public apiTokenService: ProjectApiTokenService, private toastr: ToastrService) { }

  public toggleNewScope(scope: ProjectApiTokenScopes) {
    const next = new Set(this.newTokenScopeSet);
    if (next.has(scope)) {
      next.delete(scope);
    } else {
      next.add(scope);
    }
    this.newTokenScopeSet = next;
  }

  public formValidForGeneration(): boolean {
    if (this.newTokenName.trim().length === 0 || this.newTokenScopeSet.size === 0) {
      return false;
    }
    return true;
  }

  public async generateToken() {
    const res = await this.apiTokenService.createApiToken(this.newTokenName.trim(), Array.from(this.newTokenScopeSet).sort(), this.newTokenExpiration.asSeconds());
    this.accessToken = res.token;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    this.newTokenExpiration = this.expirations[0]!.duration;
    this.newTokenName = "";
    this.newTokenScopeSet = new Set<ProjectApiTokenScopes>();
  }

  public getExpirationDay(expiration: Duration) {
    return dayjs().add(expiration).format("dddd DD.MM.YYYY");
  }

  public getExpiraitionFromToken(token: ProjectApiTokenInFrontend) {
    if (!token.exp || !token.iat) return "Error";
    if (dayjs(token.exp * 1000).diff(dayjs(), "years") > 30) return $localize`:@@token-never-expires:Verfällt nie`;
    return $localize`:@@token-expires-at:Verfällt am` + " " + dayjs(token.exp * 1000).format("dddd DD.MM.YYYY");
  }

  public getLastUsed(token: ProjectApiTokenInFrontend) {
    if (dayjs(token.lastUsed).unix() == 0) return $localize`:@@never-used:Wurde noch nie verwendet`;
    return $localize`:@@last-used:Zuletzt verwendet` + " " + dayjs(token.lastUsed).fromNow();
  }

  public async copyToken() {
    if (!this.accessToken) return;
    await navigator.clipboard.writeText(this.accessToken);
    this.toastr.success("GENERAL", $localize`:@@copied-success:Token in Zwischenablage kopiert!`);
  }

}
